<script setup>
import ButtonSave from "@/components/common/button/ButtonSave.vue";
import NewPasswordField from "@/components/authentication/fields/NewPasswordField.vue";
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {changeUserPassword, getUser} from '@/services/api/auth'
import { EnumPasswordStrength } from '@/enums/enums.js'
import {handleLoginError} from "@/utils/errors.js";
import messageService from "@/services/messages.js";
import InputText from "@/components/common/fields/InputText.vue";

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const newPasswordFieldRef = ref(null)
const login = route.query.login
const newPassword = ref('')
const oldPassword = ref('')
const confirmPassword = ref('')
const loading = ref(false)

const showPassword = ref(false)
const showConfirmPassword = ref(false)
const user = ref('');
const form = ref(null);

const togglePassword = () => {
    showPassword.value = !showPassword.value
}
const toggleConfirmPassword = () => {
    showConfirmPassword.value = !showConfirmPassword.value
}

const passwordStrength = computed(() => {
    const passwordStrength = newPasswordFieldRef.value?.passwordStrength || EnumPasswordStrength.Weak
    return passwordStrength
})

const handleSubmit = async () => {
    user.value = await getUser()
    if (newPassword.value !== confirmPassword.value) {
        messageService.error(t('components.authentication.resetPassword.passwordsDoNotMatch'))
        return
    }

    if (passwordStrength.value !== EnumPasswordStrength.Strong) {
        messageService.error(t('common.messages.errors.invalidForce'))
        return
    }

    const { valid } = await form.value.validate()
    if (!valid) return

    loading.value = true

    try {
        const response = await changeUserPassword({
            id: user.value.id,
            current_password: oldPassword.value,
            new_password: newPassword.value
        })

        message.success(t("common.messages.success.update"))

    } catch (error) {
        let errorMessage = error.response.data.error;

        switch (errorMessage) {
            case "Old password does not match.":
                message.error(t('components.myProfile.changePassword.currentPasswordNotMatch'));
                break;
            case "The new password has already been used previously.":
                message.error(t('components.myProfile.changePassword.newPasswordAlreadyUsed'));
                break;
            default:
                message.error(await handleLoginError(error, t));
        }

    } finally {
        loading.value = false
    }
}
</script>
<template>
    <div
        class="settings-card trezo-card border-radius d-block bg-white border-0 shadow-none"
    >
        <div class="settings-card-content">
            <div class="change-password-card">
                <v-form ref="form" @submit.prevent="handleSubmit">
                    <div class="row">
                        <div class="col-sm-12">
                            <div class="trezo-form-group">
                                <v-label class="main-label d-block fw-medium text-black">
                                    {{ $t('components.authentication.resetPassword.oldPassword') }}
                                </v-label>
                                <div class="d-flex">
                                    <InputText maxlength="30" v-model="oldPassword" :type="showPassword ? 'text' : 'password'" required :label="$t('components.authentication.resetPassword.oldPasswordPlaceholder')"></InputText>
                                    <span @click="togglePassword" class="align-icon ml-2 material-icons align-content-center">
                                        {{ showPassword ? 'visibility_off' : 'visibility' }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="col-sm-12">
                            <div class="trezo-form-group ">
                                <v-label class="main-label d-block fw-medium text-black">
                                    {{ $t('components.authentication.resetPassword.newPasswordPlaceholder') }}
                                </v-label>
                                <NewPasswordField
                                    v-model="newPassword"
                                    ref="newPasswordFieldRef"
                                    :label="$t('components.authentication.resetPassword.newPasswordPlaceholder')"
                                    :confirmPassword="confirmPassword"
                                />
                            </div>
                        </div>
                        <div class="col-sm-12">
                            <div class="trezo-form-group">
                                <v-label class="main-label d-block fw-medium text-black">
                                    {{ $t('components.authentication.resetPassword.confirmPassword') }}
                                </v-label>
                                <div class="d-flex">
                                    <InputText v-model="confirmPassword" maxlength="30" :type="showConfirmPassword ? 'text' : 'password'" required :label="$t('components.authentication.resetPassword.newPasswordPlaceholder')"></InputText>
                                    <span @click="toggleConfirmPassword" class="align-icon ml-2 material-icons align-content-center">
                                        {{ showConfirmPassword ? 'visibility_off' : 'visibility' }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="form-btn-box">
                        <ButtonSave :loading="loading"  />
                    </div>
                </v-form>
            </div>
        </div>
    </div>
</template>


<style lang="scss" scoped>
.settings-card {
    .settings-card-content {
        .nav-links {
            margin-bottom: 10px;

            li {
                margin: {
                    right: 15px;
                    bottom: 15px;
                }
                a {
                    height: auto;
                    min-width: auto;
                    font-weight: 500;
                    border-radius: 7px;
                    padding: 10.5px 15px;
                    color: var(--primaryColor);
                    background-color: transparent;
                    border: 1px solid var(--primaryColor);
                    display: inline-block;

                    &.router-link-active,
                    &:hover {
                        color: var(--whiteColor);
                        background-color: var(--primaryColor);
                    }
                }
            }
        }
    }
}

/* Max width 767px */
@media only screen and (max-width: 767px) {
    .settings-card {
        .settings-card-content {
            .nav-links {
                margin-bottom: 7px;

                li {
                    margin: {
                        right: 10px;
                        bottom: 10px;
                    }
                    button {
                        padding: 10px 15px;
                    }
                }
            }
        }
    }
}

/* Min width 576px to Max width 767px */
@media only screen and (min-width: 576px) and (max-width: 767px) {
    .settings-card {
        .settings-card-content {
            .nav-links {
                margin-bottom: 10px;
            }
        }
    }
}
</style>
